export const DEFAULT_COMPLIANCE_OPTIONS = Object.freeze({
  deferred: 'excluded', unlisted: 'compliant', pending: 'compliant'
});

export const COMPLIANCE_BUCKETS = Object.freeze([
  { key: 'activeListed', label: 'Active license, listed' },
  { key: 'activeUnlisted', label: 'Active license, not listed' },
  { key: 'pendingListed', label: 'Pending license, listed' },
  { key: 'pendingUnlisted', label: 'Pending license, not listed' },
  { key: 'expired', label: 'Expired license' },
  { key: 'noLicense', label: 'No license on file' },
  { key: 'other', label: 'Other inactive / unknown' },
  { key: 'deferred', label: 'Deferred' }
]);

export function normalizeComplianceOptions(options = {}) {
  return {
    deferred: ['excluded', 'compliant', 'noncompliant'].includes(options.deferred) ? options.deferred : 'excluded',
    unlisted: options.unlisted === 'excluded' ? 'excluded' : 'compliant',
    pending: options.pending === 'noncompliant' ? 'noncompliant' : 'compliant'
  };
}

export function isDefaultComplianceOptions(options) {
  const normalized = normalizeComplianceOptions(options);
  return Object.keys(DEFAULT_COMPLIANCE_OPTIONS).every(key => normalized[key] === DEFAULT_COMPLIANCE_OPTIONS[key]);
}

const isCount = value => Number.isSafeInteger(value) && value >= 0;
export function hasComplianceBuckets(data) {
  const buckets = data?.calculationBuckets;
  return data?.definition === 'licensed-over-licensed-and-undeferred-listed-union-v3'
    && buckets != null && COMPLIANCE_BUCKETS.every(({ key }) => isCount(buckets[key]))
    && Object.keys(buckets).length === COMPLIANCE_BUCKETS.length
    && buckets.activeListed + buckets.activeUnlisted + buckets.pendingListed + buckets.pendingUnlisted === data.licensedProperties
    && buckets.expired + buckets.noLicense + buckets.other === data.unlicensedUndeferredListedProperties
    && buckets.deferred === data.excludedDeferredProperties;
}

// These are unique properties, using the most relevant license status. Never
// substitute inventory.activeLicenses, which counts individual license records.
export function dashboardLicensedPropertyCounts(data) {
  if (data?.definition !== 'licensed-over-licensed-and-undeferred-listed-union-v3') return null;
  if (Object.hasOwn(data, 'calculationBuckets')) {
    if (!hasComplianceBuckets(data)) return null;
    const buckets = data.calculationBuckets;
    return { active: buckets.activeListed + buckets.activeUnlisted, pending: buckets.pendingListed + buckets.pendingUnlisted };
  }
  // Earlier snapshots saved the status totals before recording listed/unlisted
  // intersections. Those totals still support these two independent trends.
  if (data.categoryPopulation !== 'licensed-or-listed' || !Array.isArray(data.detailedCategories)) return null;
  const details = data.detailedCategories.filter(item => item?.group === 'licensed');
  const active = details.filter(item => item.key === 'active');
  const pending = details.filter(item => item.key === 'pending');
  if (details.length !== 2 || active.length !== 1 || pending.length !== 1 || !isCount(active[0].count) || !isCount(pending[0].count)
    || !isCount(data.licensedProperties) || active[0].count + pending[0].count !== data.licensedProperties) return null;
  return { active: active[0].count, pending: pending[0].count };
}

// All buckets are disjoint properties. A property's active license takes
// precedence over pending; deferrals apply to the unlicensed deferred bucket.
export function calculatePropertyCompliance(data, selectedOptions) {
  if (data?.definition !== 'licensed-over-licensed-and-undeferred-listed-union-v3') return null;
  const options = normalizeComplianceOptions(selectedOptions);
  const customizable = hasComplianceBuckets(data);
  const groups = [
    { key: 'compliant', label: 'Compliant', value: 0, details: [] },
    { key: 'noncompliant', label: 'Non-compliant', value: 0, details: [] },
    { key: 'excluded', label: 'Excluded', value: 0, details: [] }
  ];
  const byKey = Object.fromEntries(groups.map(group => [group.key, group]));
  function add(groupKey, key, label, count) {
    if (!count) return;
    byKey[groupKey].value += count;
    byKey[groupKey].details.push({ key, label, count });
  }
  if (customizable) {
    for (const { key, label } of COMPLIANCE_BUCKETS) {
      let group = 'noncompliant';
      if (key.endsWith('Unlisted') && options.unlisted === 'excluded') group = 'excluded';
      else if (key.startsWith('active')) group = 'compliant';
      else if (key.startsWith('pending')) group = options.pending;
      else if (key === 'deferred') group = options.deferred;
      add(group, key, label, data.calculationBuckets[key]);
    }
  } else {
    // Old snapshots remain usable under their original definition. Never guess
    // the pending/unlisted overlap when a custom calculation needs it.
    if (!isDefaultComplianceOptions(options)) return null;
    const deferred = data.excludedDeferredProperties ?? data.categories?.find(item => item.key === 'deferred')?.count ?? 0;
    if (![data.licensedProperties, data.unlicensedUndeferredListedProperties, deferred].every(isCount)) return null;
    add('compliant', 'licensed', 'Active or pending licenses', data.licensedProperties);
    add('noncompliant', 'violation', 'Unlicensed, listed, not deferred', data.unlicensedUndeferredListedProperties);
    add('excluded', 'deferred', 'Deferred', deferred);
    if (data.categoryPopulation === 'licensed-or-listed' && Array.isArray(data.detailedCategories)) {
      for (const [group, oldKey] of [['compliant', 'licensed'], ['noncompliant', 'violation'], ['excluded', 'deferred']]) {
        const details = data.detailedCategories.filter(item => item.group === oldKey && isCount(item.count) && item.count > 0);
        if (details.reduce((sum, item) => sum + item.count, 0) === byKey[group].value) byKey[group].details = details;
      }
    }
  }
  const numerator = byKey.compliant.value;
  const denominator = numerator + byKey.noncompliant.value;
  return { groups, numerator, denominator, excluded: byKey.excluded.value,
    total: denominator + byKey.excluded.value, rate: denominator ? numerator / denominator : null, customizable };
}
